import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBackRounded as ArrowBackIcon,
  CloseRounded as CloseIcon,
  RefreshRounded as RefreshIcon,
  ViewInArRounded as ViewInArIcon,
} from '@mui/icons-material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { api, Sitemap3dBlock, Sitemap3dDetail, Sitemap3dProject, sitemap3dSitePlanToSitePlanData } from '../../services/api';
import type { SolarTower } from '../siteplan/solarScene';

const SolarSitePlan = React.lazy(() => import('../siteplan/SolarSitePlan'));

/** Massing colour. One tone for the whole site: there is no sales status to
 *  heat-map here, unlike the Top Viewing site plan. */
const TOWER_COLOR = '#55c7b0';

/** 2D marker colours. Stacks reuse the Top Viewing plan's green dot; a
 *  block-only marker is tinted differently so the two read apart. */
const STACK_DOT_COLOR = 'var(--color-success)';
const BLOCK_DOT_COLOR = 'var(--color-info)';

type ViewMode = '2d' | '3d' | '3ds';

interface Sitemap3dDialogProps {
  open: boolean;
  onClose: () => void;
  /** MAP-3D (or admin / PROJECT-DEPT / ACCELERATOR) — shows the 3D massing toggle. */
  canView3d?: boolean;
  /** MAP-3DS (or admin / PROJECT-DEPT / ACCELERATOR) — shows the 3DS toggle (massing + sun, shadows, time of day). */
  canView3dSun?: boolean;
  /** Admins may calibrate north from inside the 3DS view. */
  isAdminUser?: boolean;
  /** Fired when a project's site plan is opened, for analytics. */
  onProjectViewed?: (project: Sitemap3dProject) => void;
}

function metaLine(project: { district: string | null; region: string | null; area: string | null }): string {
  return [
    project.district ? `D${String(project.district).replace(/^D/i, '')}` : null,
    project.region,
    project.area,
  ].filter(Boolean).join(' · ');
}

/**
 * 3D Sitemap (agent view): the projects an admin has switched on, each with its
 * own site plan. Picking one opens the plan in 2D, and — depending on the
 * session's MAP-3D / MAP-3DS grant — the same 3D massing and Solar Explorer the
 * Top Viewing Site Plans tab renders for new launches.
 */
const Sitemap3dDialog: React.FC<Sitemap3dDialogProps> = ({
  open,
  onClose,
  canView3d = false,
  canView3dSun = false,
  isAdminUser = false,
  onProjectViewed,
}) => {
  const [projects, setProjects] = useState<Sitemap3dProject[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [selected, setSelected] = useState<Sitemap3dProject | null>(null);
  const [detail, setDetail] = useState<Sitemap3dDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [viewModeSelection, setViewMode] = useState<ViewMode>('2d');
  const [showStacks, setShowStacks] = useState(true);
  // Same dot metrics the Top Viewing site plan uses, so the two read alike.
  const isMobileSitePlan = useMediaQuery('(max-width: 900px)');
  const dotSize = isMobileSitePlan ? 14 : 28;
  const dotFontSize = isMobileSitePlan ? '0.45rem' : '0.65rem';
  const dotBorder = isMobileSitePlan ? '1px solid var(--color-white)' : '2px solid var(--color-white)';
  const dotShadow = isMobileSitePlan
    ? '0 1px 2px rgb(var(--color-shadow-rgb) / 0.25)'
    : '0 2px 4px rgb(var(--color-shadow-rgb) / 0.3)';

  // A selection without the matching grant resolves to 2D, mirroring the
  // Top Viewing site plan. The server gates the north read separately.
  const viewMode: ViewMode =
    (viewModeSelection === '3d' && canView3d) || (viewModeSelection === '3ds' && canView3dSun)
      ? viewModeSelection
      : '2d';

  const loadProjects = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      setProjects(await api.getSitemap3dProjects());
    } catch (error) {
      setListError(error instanceof Error ? error.message : 'Failed to load the 3D Sitemap');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadProjects();
  }, [open, loadProjects]);

  useEffect(() => {
    if (open) return;
    // Reset so reopening lands back on the project list.
    setSelected(null);
    setDetail(null);
    setDetailError('');
    setViewMode('2d');
  }, [open]);

  const openProject = useCallback(async (project: Sitemap3dProject) => {
    setSelected(project);
    setDetail(null);
    setDetailError('');
    setViewMode('2d');
    setDetailLoading(true);
    onProjectViewed?.(project);
    try {
      setDetail(await api.getSitemap3dProject(project.projectId));
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : 'Failed to load the site plan');
    } finally {
      setDetailLoading(false);
    }
  }, [onProjectViewed]);

  /** Everything plotted — block markers included. The 2D overlay draws them all. */
  const plotted: Sitemap3dBlock[] = useMemo(() => detail?.sitePlan?.blocks || [], [detail]);

  /** Only stacks carry a storey count, so only stacks become massing. */
  const stacks = useMemo(() => plotted.filter((block) => Boolean(block.stack)), [plotted]);

  /** The plotted stacks as massing boxes. Heights come from the storey count
   *  the admin typed in while plotting (`floors`); the scene normalizes them
   *  against the tallest block on the site. */
  const towers = useMemo<SolarTower[]>(() => stacks.map((block) => ({
    x: Number(block.x),
    y: Number(block.y),
    width: Number(block.width),
    depth: Number(block.height),
    floors: Number(block.floors) || 0,
    blockNumber: block.blockNumber || '',
    label: block.stack || '',
    color: TOWER_COLOR,
    visible: true,
    blinking: false,
    onClick: () => undefined,
  })), [stacks]);

  const renderList = () => {
    if (listLoading) {
      return (
        <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (listError) {
      return (
        <Alert severity="error" sx={{ m: 3 }} action={<Button onClick={() => void loadProjects()}>Retry</Button>}>
          {listError}
        </Alert>
      );
    }
    if (projects.length === 0) {
      return (
        <Box sx={{ p: 8, textAlign: 'center' }}>
          <ViewInArIcon sx={{ fontSize: 48, color: 'rgba(var(--color-text-rgb), 0.3)' }} />
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            No site plans yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            An admin adds projects to this module from Manage 3D Sitemap.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        }}
      >
        {projects.map((project) => (
          <Card
            key={project.projectId}
            sx={{
              bgcolor: 'var(--color-surface)',
              border: '1px solid rgba(var(--color-text-rgb), 0.08)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardActionArea onClick={() => void openProject(project)}>
              {project.thumbnailUrl ? (
                <Box
                  component="img"
                  src={project.thumbnailUrl}
                  alt={project.projectName}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    display: 'block',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                    color: 'rgba(var(--color-text-rgb), 0.5)',
                  }}
                >
                  <Typography variant="body2">Site Plan Not Available</Typography>
                </Box>
              )}
              <Box sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {project.projectName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                  {metaLine(project) || project.location || '—'}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label={`${project.stackCount} stack${project.stackCount === 1 ? '' : 's'}`} />
                  {project.topDate && <Chip size="small" variant="outlined" label={`TOP ${project.topDate}`} />}
                  {!project.isPublished && <Chip size="small" color="warning" label="Draft" />}
                </Stack>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    );
  };

  const renderDetail = () => {
    if (detailLoading) {
      return (
        <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (detailError) {
      return <Alert severity="error" sx={{ m: 3 }}>{detailError}</Alert>;
    }
    const sitePlan = detail?.sitePlan;
    if (!sitePlan?.imageUrl) {
      return (
        <Alert severity="info" sx={{ m: 3 }}>
          No site plan has been uploaded for this project yet.
        </Alert>
      );
    }

    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              {detail?.project.projectName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              {[metaLine(detail!.project), detail?.project.location].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {viewMode === '2d' && plotted.length > 0 && (
              <FormControlLabel
                control={<Switch size="small" checked={showStacks} onChange={(event) => setShowStacks(event.target.checked)} />}
                label={<Typography variant="caption">Stacks</Typography>}
              />
            )}
            {(canView3d || canView3dSun) && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={viewMode}
                onChange={(_, value) => value && setViewMode(value)}
                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', fontWeight: 600 } }}
              >
                <ToggleButton value="2d">2D</ToggleButton>
                {canView3d && <ToggleButton value="3d">3D</ToggleButton>}
                {canView3dSun && <ToggleButton value="3ds">3DS</ToggleButton>}
              </ToggleButtonGroup>
            )}
          </Stack>
        </Box>

        {viewMode !== '2d' && stacks.length === 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            No stacks have been plotted yet, so the massing model is empty.
          </Alert>
        )}

        {viewMode !== '2d' ? (
          <React.Suspense fallback={<Box sx={{ p: 6, textAlign: 'center' }}>Loading 3D site plan…</Box>}>
            <SolarSitePlan
              key={`${detail?.project.projectId}-${sitePlan.imageUrl}-${viewMode}`}
              site={sitemap3dSitePlanToSitePlanData(sitePlan)}
              projectId={detail!.project.projectId}
              latitude={detail?.project.mapLatitude ?? null}
              longitude={detail?.project.mapLongitude ?? null}
              towers={towers}
              initialNorthAngle={detail?.northAngle}
              onFallback={() => setViewMode('2d')}
              sunlight={viewMode === '3ds'}
              canCalibrateNorth={isAdminUser}
              canViewNearbyMap={isAdminUser}
              projectName={detail?.project.projectName}
              totalUnits={detail?.project.totalUnits}
            />
          </React.Suspense>
        ) : (
          <Box
            sx={{
              position: 'relative',
              border: '1px solid rgba(var(--color-text-rgb), 0.12)',
              borderRadius: 1,
              overflow: 'hidden',
              height: 'min(680px, 68vh)',
              bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
            }}
          >
            <TransformWrapper initialScale={1} minScale={0.6} maxScale={5} centerOnInit limitToBounds centerZoomedOut>
              {/* The image sizes the content box, so the percentage overlay lands
                  on the painted pixels rather than on letterboxing. */}
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%' }}>
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <Box
                    component="img"
                    src={sitePlan.imageUrl}
                    alt={detail?.project.projectName || 'Site plan'}
                    sx={{ width: '100%', display: 'block' }}
                  />
                  {/* A dot centred in each plotted footprint — the same marker
                      the Top Viewing site plan draws, rather than an outline. */}
                  {showStacks && plotted.map((block, index) => (
                    <Box
                      key={block.id ?? `${block.blockNumber || ''}-${block.stack || ''}-${index}`}
                      sx={{
                        position: 'absolute',
                        left: `${block.x}%`,
                        top: `${block.y}%`,
                        width: `${block.width}%`,
                        height: `${block.height}%`,
                        pointerEvents: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: dotSize,
                          height: dotSize,
                          borderRadius: '50%',
                          backgroundColor: block.stack ? STACK_DOT_COLOR : BLOCK_DOT_COLOR,
                          border: dotBorder,
                          boxShadow: dotShadow,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: dotFontSize,
                          fontWeight: 'bold',
                          color: 'var(--color-white)',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                          zIndex: 10,
                        }}
                      >
                        {block.stack || block.blockNumber}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </TransformComponent>
            </TransformWrapper>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // `--color-background` is not a token in index.css — leaving it there gave
      // the Paper no background at all, so the app showed straight through the
      // dialog. `--color-surface` is the real one.
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          m: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          bgcolor: 'var(--color-surface)',
        }}
      >
        {selected ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => { setSelected(null); setDetail(null); setDetailError(''); }}
          >
            Back to list
          </Button>
        ) : (
          <ViewInArIcon sx={{ color: 'var(--color-accent)' }} />
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1, color: 'var(--color-text-strong)' }}>
          3D Sitemap
        </Typography>
        {!selected && (
          <IconButton size="small" onClick={() => void loadProjects()} aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {selected ? renderDetail() : renderList()}
      </Box>
    </Dialog>
  );
};

export default Sitemap3dDialog;
